import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const FavoritesContext = createContext(null);
const empty = () => ({ audio: new Set(), scenepack: new Set() });

/** Which audios / scenepacks the signed-in user has bookmarked. */
export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(empty);

  useEffect(() => {
    if (!user) { setIds(empty()); return; }
    api.get("/favorites/ids")
      .then((r) => setIds({ audio: new Set(r.data.audio), scenepack: new Set(r.data.scenepack) }))
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFavorite = useCallback((kind, id) => ids[kind]?.has(id) || false, [ids]);

  const setOne = (kind, id, on) =>
    setIds((prev) => {
      const next = new Set(prev[kind]);
      if (on) next.add(id); else next.delete(id);
      return { ...prev, [kind]: next };
    });

  const toggleFavorite = useCallback(async (kind, id) => {
    if (!user) { toast("Sign in to save favourites"); return; }
    const was = ids[kind]?.has(id) || false;
    setOne(kind, id, !was); // optimistic
    try {
      const { data } = await api.post(`/favorites/${kind}/${id}`);
      setOne(kind, id, data.favorited);
      toast.success(data.favorited ? "Added to favourites" : "Removed from favourites");
    } catch (err) {
      setOne(kind, id, was);
      toast.error(err.response?.data?.detail || "Could not update favourites");
    }
  }, [user, ids]);

  return (
    <FavoritesContext.Provider value={{ isFavorite, toggleFavorite }}>{children}</FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
